import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Alert,
  Box,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  MenuItem,
  Paper,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { AppDispatch, RootState } from '../../../Redux/Store';
import {
  fetchReservationSetting,
  fetchSeatTypes,
  saveReservationSetting,
} from '../../../Redux/ReservationSettings/reservationSettingsApi';
import { CapacityMode, ReservationSetting } from '../../../types/reservation';
import NumberField from '../../../Components/NumberInput/NumberField';
import {
  SavedSnackbar,
  TabDescription,
  UnsavedChangesBar,
  useDirtyNotifier,
  useSaveFeedback,
} from './SettingsShared';

/** 受付可能期間（何日先まで）のプリセット。カスタム選択時のみ日数入力を出す。 */
const UNTIL_DAYS_PRESETS: { value: number; label: string }[] = [
  { value: 31, label: '1ヶ月' },
  { value: 92, label: '3ヶ月' },
  { value: 183, label: '6ヶ月' },
  { value: 365, label: '1年' },
];

interface Props {
  storeId: string;
  /** dirty（サーバ値と差分あり）の変化を親へ通知する（タブ切替時の確認用） */
  onDirtyChange?: (dirty: boolean) => void;
}

/**
 * タブ①受付設定。GET/PUT /stores/{id}/reservation-setting。
 * ReservationSetting の全項目をフォームで編集し、保存で丸ごと PUT する。
 * 未保存の変更がある間は画面下部に sticky 保存バーを表示する（フィードバック3）。
 */
const AcceptanceSettingTab: React.FC<Props> = ({ storeId, onDirtyChange }) => {
  const dispatch = useDispatch<AppDispatch>();
  const setting = useSelector((state: RootState) => state.reservationSettings.setting);
  const settingLoading = useSelector(
    (state: RootState) => state.reservationSettings.settingLoading,
  );
  // 一度も保存されていない店舗（BE が 404）。true の間は公開予約の受付が始められない
  const neverSaved = useSelector(
    (state: RootState) => state.reservationSettings.settingNeverSaved,
  );
  const seatTypes = useSelector((state: RootState) => state.reservationSettings.seatTypes);

  // ローカル編集用のフォーム状態。サーバ値の取得完了時に同期する。
  const [form, setForm] = useState<ReservationSetting | null>(null);
  // 受付可能期間のプリセット選択状態（'custom' 時のみ日数入力を表示）
  const [fromPreset, setFromPreset] = useState<number | 'custom'>('custom');
  const [saving, setSaving] = useState(false);
  const { savedOpen, closeSaved, showSaved, error, setError, showError } = useSaveFeedback();

  useEffect(() => {
    // SEAT_TYPE 選択時の警告判定に席タイプ一覧も必要なため同時に取得する
    dispatch(fetchReservationSetting(storeId));
    dispatch(fetchSeatTypes(storeId));
  }, [dispatch, storeId]);

  // dirty 判定・破棄の基準となるサーバ値（旧BE 未返却項目の既定補完込み）
  const baseline = useMemo<ReservationSetting | null>(
    () =>
      setting
        ? {
            ...setting,
            publicPageEnabled: setting.publicPageEnabled ?? true,
            courseSelectionRequired: setting.courseSelectionRequired ?? false,
            taxRatePercent: setting.taxRatePercent ?? 10,
          }
        : null,
    [setting],
  );

  useEffect(() => {
    if (baseline && setting) {
      setForm(baseline);
      setFromPreset(
        UNTIL_DAYS_PRESETS.some((p) => p.value === setting.acceptanceUntilDays)
          ? setting.acceptanceUntilDays
          : 'custom',
      );
    }
  }, [baseline, setting]);

  const dirty =
    form !== null && baseline !== null && JSON.stringify(form) !== JSON.stringify(baseline);
  useDirtyNotifier(dirty, onDirtyChange);

  /**
   * 承認期限 vs 受付締切の整合警告（フィードバック4 B4・非ブロック）。
   * 承認期限=時間 / 受付締切=分 の単位差に注意。承認制(MANUAL)かつ承認期限>0 のとき、
   * 受付締切が承認期限より4時間以上手前でない（cutoffMinutes/60 < sla + 4）なら推奨警告を出す。
   * 保存はブロックしない（お伝えするだけ）。
   */
  const cutoffSlaWarning = useMemo<string | null>(() => {
    if (!form) return null;
    if (form.approvalMode !== 'MANUAL') return null;
    const sla = form.requestApprovalSlaHours;
    if (!sla || sla <= 0) return null;
    const cutoffHours = form.acceptanceCutoffMinutes / 60;
    if (cutoffHours >= sla + 4) return null;
    // 現在の差（受付締切 − 承認期限）を約◯時間で示す（負なら締切のほうが手前でない）
    const diffHours = Math.round(cutoffHours - sla);
    return `受付締切は承認期限より4時間以上前に設定することを推奨します（承認の時間を十分に確保するため）。現在の差は約${diffHours}時間です。`;
  }, [form]);

  if (settingLoading || !form) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  /** number 項目の共通入力（先頭0対策の NumberField を使う） */
  const numField = (
    key: keyof ReservationSetting,
    label: string,
    helper?: string,
  ): React.ReactElement => (
    <NumberField
      label={label}
      value={form[key] as number}
      onChangeValue={(v) => setForm({ ...form, [key]: v })}
      min={0}
      helperText={helper}
      fullWidth
      margin="dense"
      size="small"
    />
  );

  const handleFromPresetChange = (_: unknown, value: number | 'custom' | null) => {
    if (value === null) return; // トグル解除は無視（常にどれかが選択された状態を保つ）
    setFromPreset(value);
    if (value !== 'custom') {
      setForm({ ...form, acceptanceUntilDays: value });
    }
  };

  const handleCapacityModeChange = (_: unknown, value: CapacityMode | null) => {
    if (value === null) return;
    setForm({ ...form, capacityMode: value });
  };

  const save = () => {
    // SEAT_TYPE モードは席タイプが 1 件以上ないと BE 側でも保存できない
    if (form.capacityMode === 'SEAT_TYPE' && seatTypes.length === 0) {
      setError('席タイプ×卓数で管理するには、席・在庫設定タブで席タイプを1件以上登録してください。');
      return;
    }
    setSaving(true);
    const payload: ReservationSetting = {
      ...form,
      mailCustomMessage: form.mailCustomMessage?.trim() ? form.mailCustomMessage.trim() : null,
    };
    dispatch(saveReservationSetting({ storeId, setting: payload }))
      .unwrap()
      .then(() => showSaved())
      .catch(showError)
      .finally(() => setSaving(false));
  };

  /**
   * 無制限チェックボックス付きの上限入力（最大組数/最大人数）。
   * チェック ON = 0 を送信（BE 契約: 0=無制限）。OFF 時のみ数値入力を出す。
   */
  const unlimitedField = (
    key: 'maxPartiesPerSlot' | 'maxPeoplePerSlot',
    label: string,
    defaultWhenLimited: number,
  ): React.ReactElement => (
    <Box>
      <FormControlLabel
        control={
          <Checkbox
            checked={form[key] === 0}
            onChange={(e) =>
              setForm({ ...form, [key]: e.target.checked ? 0 : defaultWhenLimited })
            }
          />
        }
        label={`${label}を制限しない（無制限）`}
      />
      {form[key] !== 0 && (
        <NumberField
          label={label}
          value={form[key]}
          onChangeValue={(v) => setForm({ ...form, [key]: v })}
          min={1}
          fullWidth
          margin="dense"
          size="small"
        />
      )}
    </Box>
  );

  return (
    <Box>
      <TabDescription text="お客様が予約できる時間の刻み・人数・期間を設定します。保存した内容は新しい予約から適用されます" />

      {/* 一度も保存されていない店舗への警告（保存するまで公開予約を受け付けられない） */}
      {neverSaved && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          受付設定がまだ一度も保存されていません。保存するまで公開予約の受付を開始できません。内容をご確認のうえ「保存」してください。
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* 予約ページの公開/非公開（フィードバック1・最上部に配置） */}
      <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
        <FormControlLabel
          control={
            <Switch
              checked={form.publicPageEnabled ?? true}
              onChange={(e) => setForm({ ...form, publicPageEnabled: e.target.checked })}
            />
          }
          label="予約ページを公開する"
        />
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          OFFにすると新規予約の受付を停止します。すでに入っている予約には影響しません。
        </Typography>
      </Paper>

      <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          承認・受付方法
        </Typography>
        <TextField
          select
          label="予約の確定方法"
          value={form.approvalMode}
          onChange={(e) =>
            setForm({ ...form, approvalMode: e.target.value as ReservationSetting['approvalMode'] })
          }
          fullWidth
          margin="dense"
          size="small"
        >
          <MenuItem value="AUTO">自動で確定する（空きがあれば即確定）</MenuItem>
          <MenuItem value="MANUAL">店舗が確認して確定する</MenuItem>
        </TextField>
        {/* 承認期限は「店舗が確認して確定する」選択時のみ意味を持つため、その時だけ表示する */}
        {form.approvalMode === 'MANUAL' &&
          numField(
            'requestApprovalSlaHours',
            '承認期限（時間）',
            '期限内に確定しなかった申込は自動でお断りし、お客様へメールでお知らせします（0=自動キャンセルしない）',
          )}
        {numField('slotIntervalMinutes', '予約枠間隔（分）')}
      </Paper>

      <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          空き管理方法
        </Typography>
        <ToggleButtonGroup
          value={form.capacityMode}
          exclusive
          onChange={handleCapacityModeChange}
          size="small"
          fullWidth
          sx={{ mb: 1 }}
        >
          <ToggleButton value="AGGREGATE">組数・人数の上限で管理（シンプル）</ToggleButton>
          <ToggleButton value="SEAT_TYPE">席タイプ×卓数で管理</ToggleButton>
        </ToggleButtonGroup>
        {form.capacityMode === 'AGGREGATE' && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            下の最大組数・最大人数の上限だけで空きを判定します。
          </Typography>
        )}
        {form.capacityMode === 'SEAT_TYPE' && (
          <Alert severity="info" sx={{ mb: 1 }}>
            席タイプで管理中: <strong>席・在庫タブの内容が予約の空き判定に使われます</strong>
            （席タイプ×卓数、必要に応じて連結）。下の最大組数・最大人数は任意の全体上限として補助的に働きます。
          </Alert>
        )}
        {form.capacityMode === 'SEAT_TYPE' && seatTypes.length === 0 && (
          <Alert severity="warning" sx={{ mb: 1 }}>
            席タイプが未登録です。席・在庫設定タブで席タイプを1件以上登録してください。
          </Alert>
        )}
        {/* SEAT_TYPE 時は組数/人数上限を副次扱い（見た目も控えめ）にする */}
        <Box
          sx={
            form.capacityMode === 'SEAT_TYPE'
              ? { opacity: 0.7, pl: 1, borderLeft: '2px solid', borderColor: 'divider' }
              : undefined
          }
        >
          {form.capacityMode === 'SEAT_TYPE' && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              以下は任意の全体上限（必要なければ「無制限」のままで構いません）
            </Typography>
          )}
          {unlimitedField('maxPartiesPerSlot', '同じ時間帯の最大組数', 5)}
          {unlimitedField('maxPeoplePerSlot', '同じ時間帯の最大人数', 20)}
        </Box>
        {numField('minPartySize', '1組の最小人数')}
        {numField('maxPartySize', '1組の最大人数')}
        {numField(
          'defaultStayMinutes',
          'デフォルト滞在時間（分）',
          'コースに滞在時間が設定されていない場合に使われます（既定120分）',
        )}
      </Paper>

      <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          受付可能期間・締切
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
          何日先まで予約を受け付けるか
        </Typography>
        <ToggleButtonGroup
          value={fromPreset}
          exclusive
          onChange={handleFromPresetChange}
          size="small"
          sx={{ mb: 1, flexWrap: 'wrap' }}
        >
          {UNTIL_DAYS_PRESETS.map((p) => (
            <ToggleButton key={p.value} value={p.value}>
              {p.label}
            </ToggleButton>
          ))}
          <ToggleButton value="custom">カスタム</ToggleButton>
        </ToggleButtonGroup>
        {fromPreset === 'custom' &&
          numField('acceptanceUntilDays', '受付可能期間（日数）', '例: 60=60日先まで受付')}
        {numField('acceptanceFromDays', '受付開始（何日先から）', '通常は0=当日から。前日以降のみ受付なら1')}
        {numField('acceptanceCutoffMinutes', '受付締切（予約時刻の何分前まで）')}
        {/* 承認期限 vs 受付締切の推奨警告（非ブロック・フィードバック4 B4） */}
        {cutoffSlaWarning && (
          <Alert severity="warning" sx={{ mt: 1 }}>
            {cutoffSlaWarning}
          </Alert>
        )}
      </Paper>

      <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          顧客向け設定
        </Typography>
        {numField('reminderOffsetHours', 'リマインダー送信（予約時刻の何時間前）', '0=送らない')}
        {numField(
          'selfCancelDeadlineHours',
          'セルフキャンセル期限（予約時刻の何時間前まで）',
          'お客様自身でキャンセルできる期限です（0=いつでも可）',
        )}
        <FormControlLabel
          control={
            <Switch
              checked={form.phoneRequired}
              onChange={(e) => setForm({ ...form, phoneRequired: e.target.checked })}
            />
          }
          label="公開予約フォームで電話番号を必須にする"
        />
        {/* コース選択の必須化（フィードバック4）。オフのとき席のみ予約が可能 */}
        <Box sx={{ mt: 1 }}>
          <FormControlLabel
            control={
              <Switch
                checked={form.courseSelectionRequired ?? false}
                onChange={(e) =>
                  setForm({ ...form, courseSelectionRequired: e.target.checked })
                }
              />
            }
            label="コース選択を必須にする"
          />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            オフのとき、お客様はコースを選ばず席のみの予約ができます
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            コース全額を事前決済に設定している場合、必須にしないとコース未選択の予約では決済が行われません。
          </Typography>
        </Box>
        {/* 消費税率（フィードバック4）。税込表示・税込プレビューの算出に使う（1〜99） */}
        <NumberField
          label="消費税率（%）"
          value={form.taxRatePercent ?? 10}
          // 1〜99 に丸める（0 や 100 以上は無効）
          onChangeValue={(v) => setForm({ ...form, taxRatePercent: v })}
          min={1}
          max={99}
          helperText="お客様に表示する税込金額の計算に使います（コース価格・デポジットは税抜で入力）"
          fullWidth
          margin="dense"
          size="small"
        />
        <TextField
          label="確認メールに差し込む店舗からの案内文（任意）"
          value={form.mailCustomMessage ?? ''}
          onChange={(e) =>
            setForm({ ...form, mailCustomMessage: e.target.value === '' ? null : e.target.value })
          }
          multiline
          minRows={3}
          fullWidth
          margin="dense"
          size="small"
        />
      </Paper>

      {/* 未保存の変更がある間だけ表示される sticky 保存バー（保存し忘れ防止） */}
      <UnsavedChangesBar
        dirty={dirty}
        saving={saving}
        // 未保存店舗は変更がなくても保存バーを出し、初回保存（＝行作成）の導線にする
        forceShow={neverSaved}
        onSave={save}
        onDiscard={() => {
          if (baseline && setting) {
            setForm(baseline);
            setFromPreset(
              UNTIL_DAYS_PRESETS.some((p) => p.value === setting.acceptanceUntilDays)
                ? setting.acceptanceUntilDays
                : 'custom',
            );
          }
          setError(null);
        }}
      />

      <SavedSnackbar open={savedOpen} onClose={closeSaved} />
    </Box>
  );
};

export default AcceptanceSettingTab;
